import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from "@angular/core";
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexStroke,
    ApexTitleSubtitle,
    ApexXAxis,
} from "ng-apexcharts";
import { combineLatest, EMPTY, Subject } from "rxjs";
import { catchError, finalize, switchMap, takeUntil } from "rxjs/operators";
import { GetMobileStatsRequest, MobileApi, MonthlyActiveUsersHistoryItem } from "../api/proxies";
import { StatsService } from "../services/stats.service";
import { NotifyService } from "@ui/notify";

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
};

@Component({
    selector: "mau-history",
    templateUrl: "./mau-history.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MauHistoryComponent implements OnInit, OnDestroy {
    private _destroy$ = new Subject<void>();

    avg = 0;

    chartOptions!: Partial<ChartOptions>;

    loading = false;

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _mobileApi: MobileApi,
        private statsService: StatsService,
        private _notifyService: NotifyService
    ) {}

    ngOnInit(): void {
        combineLatest([this.statsService.period$, this.statsService._tenant$])
            .pipe(
                takeUntil(this._destroy$),
                switchMap(([period, tenant]) => {
                    this.loading = true;
                    const req = new GetMobileStatsRequest();
                    req.start = period.periodStart;
                    req.end = period.periodEnd;
                    req.tenantId = tenant?.id;

                    return this._mobileApi.mauHistory(req).pipe(
                        catchError((err) => {
                            this._notifyService.error("Falha ao carregar histórico MAU");
                            return EMPTY;
                        }),
                        finalize(() => {
                            this.loading = false;
                            this._changeDetector.markForCheck();
                        })
                    );
                })
            )
            .subscribe((data: MonthlyActiveUsersHistoryItem[]) => {
                const series: ApexAxisChartSeries = [
                    { name: "Android", data: data.map((i) => i.google), color: "#50992d" },
                    { name: "iOS", data: data.map((i) => i.apple), color: "#4587ec" },
                    { name: "PWA", data: data.map((i) => i.pwa), color: "#ff7f50" },
                    { name: "Site", data: data.map((i) => i.site), color: "#9400d3" },
                ];

                this.chartOptions = {
                    series,
                    chart: { height: 350, type: "bar", toolbar: { show: false } },
                    dataLabels: { enabled: true },
                    stroke: { curve: "smooth" },
                    xaxis: {
                        type: "datetime",
                        categories: data.map((i) => i.date.toLocaleDateString()),
                    },
                };

                let sum = 0,
                    total = 0;
                series.forEach((s) => {
                    total = Math.max(total, s.data.length);
                    s.data.forEach((v) => (sum += v));
                });
                this.avg = Math.trunc(sum / total);

                this._changeDetector.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
